import React from "react";
import { styled } from "@linaria/react";
import type { Project } from "../../../api/useProjects";
import Spinner from "@ui/Spinner";
import useActiveAnalysesStream from "../../../api/useActiveAnalysesStream";
import { analysisStep } from "diagnostics-domain/analysisStep";
import t from "../../../i18n";
const StyledSpinner = styled(Spinner)`
  width: 16px;
`;

const PROJECT_SYNC_STEPS = new Set([
  "EVALUATE_SIMULATION_PROCESS",
  "EVALUATE_EXTERNAL_GAP_PROCESSES",
  "EVALUATE_DIAGNOSIS_PROCESS",
]);

const ProjectSpinner = ({ project }: { project: Project }) => {
  const { activeAnalyses } = useActiveAnalysesStream(project.id);

  const activeAnalysisCount = activeAnalyses.length;
  const syncSteps = React.useMemo(
    () =>
      activeAnalyses
        .map(a => analysisStep(project, a))
        .filter((step): step is NonNullable<typeof step> => step !== null)
        .filter(step => PROJECT_SYNC_STEPS.has(step)),
    [project, activeAnalyses],
  );
  const outdatedCount = syncSteps.length;
  const simulationProcessCount = syncSteps.filter(
    step => step === "EVALUATE_SIMULATION_PROCESS",
  ).length;
  const externalGapProcessesCount = syncSteps.filter(
    step => step === "EVALUATE_EXTERNAL_GAP_PROCESSES",
  ).length;
  const diagnosisProcessCount = syncSteps.filter(
    step => step === "EVALUATE_DIAGNOSIS_PROCESS",
  ).length;

  const wording = React.useCallback(
    (count: number, step: string) =>
      count === 0
        ? null
        : t("app.header.project_spinner.awaiting_step_evaluation", {
            percentage: Math.ceil((count / outdatedCount) * 100),
            step,
          }),
    [outdatedCount],
  );

  const outdatedAnalyses = React.useMemo(
    () =>
      outdatedCount
        ? t("app.header.project_spinner.update_in_progress_for_analyses", {
            outdatedCount,
            activeAnalysisCount,
          }) +
          "\n\n" +
          [
            wording(simulationProcessCount, t("app.header.project_spinner.step.simulation_inputs")),
            wording(
              externalGapProcessesCount,
              t("app.header.project_spinner.step.external_gap_inputs"),
            ),
            wording(diagnosisProcessCount, t("app.header.project_spinner.step.diagnosis_inputs")),
          ]
            .flatMap(chunk => (chunk ? [chunk] : []))
            .join("\n")
        : null,
    [
      wording,
      outdatedCount,
      activeAnalysisCount,
      simulationProcessCount,
      externalGapProcessesCount,
      diagnosisProcessCount,
    ],
  );

  const outdatedProject =
    project.initInProgress === false
      ? null
      : t("app.header.project_spinner.project_initialization_in_progress");

  const outdatedGlobal = outdatedProject ?? outdatedAnalyses;

  return outdatedGlobal ? (
    <span title={outdatedGlobal}>
      <StyledSpinner />
    </span>
  ) : null;
};

export default React.memo(ProjectSpinner);
